import { useEffect, useState, type ReactElement } from 'react';

import { ApiError, apiFetch } from '../lib/api';
import { rememberGuild } from '../store/channels';
import { guildInitials } from '../components/GuildBar';

/**
 * `/invite/<code>` — the door into one server.
 *
 * The server is named before anything is offered: asking somebody to join a
 * place nobody named would be charging a trust the invite does not need to
 * charge. The name comes from `GET /api/invites/:code`, which spends nothing —
 * the use is only counted when the button is pressed.
 *
 * Somebody who is already a member is simply taken there. The link led to a
 * server they belong to, which is exactly what they asked for, and answering
 * "this invite was already used" would be an error where there is none.
 *
 * A visitor with no session never reaches this component: the router shows the
 * login screen while the address stays on the invite, so signing in brings
 * this screen back on its own.
 */

const GENERIC_FAILURE = 'Não foi possível abrir este convite. Tente de novo.';

interface InvitePreview {
  code: string;
  guild: { id: string; name: string; iconUrl: string | null };
  alreadyMember: boolean;
}

/**
 * Leaves for the app with that server open.
 *
 * Which server is remembered the same way the switcher remembers it, so the
 * shell that mounts next reads it back — no server id ever needs to travel in
 * the address of the app itself.
 */
function openGuild(guildId: string): void {
  rememberGuild(guildId);
  window.history.pushState({}, '', '/');
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export interface InvitePageProps {
  code: string;
}

export function InvitePage({ code }: InvitePageProps): ReactElement {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    let active = true;

    apiFetch<InvitePreview>(`/api/invites/${encodeURIComponent(code)}`)
      .then((found) => {
        if (!active) {
          return;
        }

        if (found.alreadyMember) {
          openGuild(found.guild.id);
          return;
        }

        setPreview(found);
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
        }
      });

    return () => {
      active = false;
    };
  }, [code]);

  async function join(): Promise<void> {
    setJoining(true);
    setError(null);

    try {
      const { guildId } = await apiFetch<{ guildId: string; alreadyMember: boolean }>(
        `/api/invites/${encodeURIComponent(code)}/accept`,
        { method: 'POST' },
      );
      openGuild(guildId);
    } catch (cause) {
      // A code can run out between reading this screen and pressing the
      // button; the refusal the API wrote is what says so.
      setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
      setJoining(false);
    }
  }

  if (error !== null && preview === null) {
    return (
      <main className="auth-screen">
        <div className="auth-card">
          <h1 className="auth-title">Convite</h1>
          <p className="auth-alert" role="alert">
            {error}
          </p>
          <a className="auth-footnote" href="/">
            Voltar para o Discordo
          </a>
        </div>
      </main>
    );
  }

  if (preview === null) {
    return (
      <main className="auth-screen">
        <p className="auth-subtitle">Carregando convite…</p>
      </main>
    );
  }

  return (
    <main className="auth-screen">
      <div className="auth-card">
        <p className="auth-subtitle">Você foi convidado para</p>

        <div className="invite-guild">
          {preview.guild.iconUrl === null ? (
            <span className="guild-icon is-large" aria-hidden="true">
              {guildInitials(preview.guild.name)}
            </span>
          ) : (
            <img className="guild-icon is-large" src={preview.guild.iconUrl} alt="" />
          )}
          <h1 className="auth-title">{preview.guild.name}</h1>
        </div>

        {error !== null && (
          <p className="auth-alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="auth-submit"
          disabled={joining}
          onClick={() => {
            void join();
          }}
        >
          {joining ? 'Entrando…' : 'Entrar no servidor'}
        </button>
      </div>
    </main>
  );
}

export default InvitePage;
